import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Key, Plus, Loader2, Trash2, Copy, Check, AlertTriangle, X, BookOpen } from "lucide-react";

/**
 * Team-tier Personal Access Tokens — create / list / revoke.
 * Raw key is shown ONCE on creation (server doesn't store the raw value).
 */
export default function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [reveal, setReveal] = useState(null); // {raw_key, name}
  const [gateMessage, setGateMessage] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/keys");
      setKeys(r.data || []);
      setGateMessage(null);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Failed to load keys");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!newName.trim()) {
      toast.error("Give the key a name first");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/keys", { name: newName.trim() });
      setReveal({ raw_key: r.data.raw_key, name: r.data.name });
      setNewName("");
      refresh();
    } catch (err) {
      if (err?.response?.status === 403) {
        setGateMessage(err?.response?.data?.detail || "Upgrade to Team to unlock API access.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not create key");
      }
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any integration using this key will stop working immediately.`)) return;
    try {
      await api.delete(`/keys/${k.id}`);
      toast.success("Key revoked");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="card" data-testid="settings-api-keys-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="label-eyebrow flex items-center gap-1.5"><Key size={11} /> API access (Team tier)</div>
        <Link
          to="/v3/api-docs"
          className="text-xs text-[var(--primary)] inline-flex items-center gap-1 hover:underline whitespace-nowrap"
          data-testid="api-keys-docs-link"
        >
          <BookOpen size={11} /> Docs
        </Link>
      </div>
      <p className="text-sm text-[var(--ink-2)] mb-3 leading-relaxed">
        Personal Access Tokens let you call iSpeedToClose endpoints from your own scripts,
        Zaps, or backend integrations. Pass <code className="font-mono text-xs bg-slate-100 px-1 rounded">Authorization: Bearer pat_…</code> on any request.
      </p>

      {gateMessage ? (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2" data-testid="api-keys-gate">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>{gateMessage}</div>
        </div>
      ) : (
        <>
          <form onSubmit={create} className="flex items-center gap-2 mb-4" data-testid="api-keys-create-form">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Key name (e.g., Zapier integration)"
              maxLength={60}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="api-key-name-input"
            />
            <button type="submit" className="btn btn-primary" disabled={creating} data-testid="api-key-create-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create key
            </button>
          </form>

          {loading ? (
            <div className="text-center text-sm text-[var(--ink-3)] py-4">
              <Loader2 size={14} className="animate-spin inline" /> Loading…
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center text-sm text-[var(--ink-3)] py-4">
              No keys yet. Create one above to get started.
            </div>
          ) : (
            <ul className="space-y-1" data-testid="api-keys-list">
              {keys.map((k) => (
                <li key={k.id} className={"flex items-center justify-between gap-2 text-sm border-b last:border-b-0 py-2 " + (k.revoked_at ? "opacity-50" : "")}>
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {k.name}
                      {k.revoked_at && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 uppercase">Revoked</span>}
                    </div>
                    <div className="text-[10px] text-[var(--ink-3)] font-mono mt-0.5">
                      {k.key_prefix} · last used {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "never"}
                    </div>
                  </div>
                  {!k.revoked_at && (
                    <button
                      type="button"
                      onClick={() => revoke(k)}
                      className="text-xs px-2 py-1 hover:bg-rose-100 text-rose-700 rounded inline-flex items-center gap-1"
                      data-testid={`api-key-revoke-${k.id}`}
                    >
                      <Trash2 size={12} /> Revoke
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {reveal && <RevealModal raw={reveal.raw_key} name={reveal.name} onClose={() => setReveal(null)} />}
    </div>
  );
}

function RevealModal({ raw, name, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="api-key-reveal-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-display font-extrabold text-lg">Your new API key</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">{name}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4">
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2 mb-3">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div><strong>This is the only time you&apos;ll see the full key.</strong> Copy it now and store it somewhere safe. We hash it on the server — there&apos;s no way to recover it later.</div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-slate-900 text-slate-100 px-3 py-2 rounded break-all" data-testid="api-key-raw">{raw}</code>
            <button type="button" className="btn btn-primary" onClick={copy} data-testid="api-key-copy-btn">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end">
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="api-key-reveal-close">
            I've stored it safely
          </button>
        </div>
      </div>
    </div>
  );
}
